import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, apiFetch } from "@/context/AuthContext";
import { motion } from "framer-motion";
import {
  ArrowLeft, Loader2, Sparkles, Send, SkipForward, Wand2, CheckCircle2,
  Target, Users, Lightbulb, ListChecks, AlertTriangle, DollarSign, Cpu, Rocket, Gauge, Boxes,
} from "lucide-react";
import { toast } from "sonner";
import { Shell } from "@/components/Studio";

export default function Discovery() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    const d = await apiFetch(`/discovery/${sessionId}`);
    setData(d);
  }, [sessionId]);

  useEffect(() => {
    if (user === null) navigate(`/login?next=${encodeURIComponent(`/studio/discovery/${sessionId}`)}`, { replace: true });
  }, [user, sessionId, navigate]);

  useEffect(() => {
    if (!user) return;
    setLoadError(null);
    load().catch((e) => setLoadError(e.message || "Could not load this discovery session"));
  }, [user, load]);

  const respond = async (path, body) => {
    setBusy(true);
    try {
      const d = await apiFetch(`/discovery/${sessionId}/${path}`, { method: "POST", body });
      setData(d); setInput("");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const send = () => { if (input.trim()) respond("answer", { answer: input.trim() }); };
  const skip = () => respond("answer", { skip: true });
  const finishNow = () => respond("finish", {});

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await apiFetch(`/discovery/${sessionId}/to-project`, { method: "POST" });
      toast.success("Project created from your brief");
      navigate(`/studio/${r.project_id}`);
    } catch (e) { toast.error(e.message); setGenerating(false); }
  };

  const syncOS = async () => {
    setSyncing(true);
    toast.info("Syncing your brief into ArcaOS…");
    try {
      const r = await apiFetch(`/os/bridge/from-discovery/${sessionId}`, { method: "POST" });
      toast.success("Synced to ArcaOS — Product, Project, Bible, Roadmap & more are linked");
      navigate(`/os/projects/${r.project_id}`);
    } catch (e) { toast.error(e.message); } finally { setSyncing(false); }
  };

  if (user === undefined) return <Shell><Center><Loader2 className="animate-spin" /> Checking your session…</Center></Shell>;
  if (user === null) return <Shell><Center><Loader2 className="animate-spin" /> Redirecting to login…</Center></Shell>;
  if (loadError) return (
    <Shell right={<BackLink />}>
      <div className="max-w-lg mx-auto text-center py-24" data-testid="discovery-error">
        <div className="w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center mx-auto mb-6"><AlertTriangle className="text-rose-400" /></div>
        <h1 className="font-display font-bold text-2xl mb-2">Can’t open this discovery</h1>
        <p className="text-slate-400 mb-8">{loadError}.</p>
        <Link to="/studio" data-testid="discovery-back-btn" className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-6 py-3.5 rounded-2xl font-semibold"><ArrowLeft size={16} /> Back to Studio</Link>
      </div>
    </Shell>
  );
  if (!data) return <Shell><Center><Loader2 className="animate-spin" /> Loading discovery…</Center></Shell>;

  const ready = data.status === "ready" && data.brief;
  const pct = ready ? 100 : Math.min(100, Math.round((data.asked / data.max_questions) * 100));

  return (
    <Shell right={<BackLink />}>
      <div className="mb-2 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#00F0FF]"><Sparkles size={14} /> Smart Discovery</div>
      <h1 className="font-display font-extrabold text-3xl md:text-5xl tracking-tight">{ready ? "Your Product Brief" : "Let’s sharpen the idea"}</h1>

      {/* Progress */}
      <div className="mt-6 mb-8" data-testid="discovery-progress">
        <div className="flex items-center justify-between text-xs font-mono text-slate-500 mb-2">
          <span>{ready ? "Brief ready" : `Question ${data.asked} of up to ${data.max_questions}`}</span>
          <span>{pct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <motion.div className="h-full bg-gradient-to-r from-[#0055FF] to-[#00F0FF]" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.5 }} />
        </div>
      </div>

      {/* Idea */}
      <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5 mb-4">
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Your idea</p>
        <p className="text-white" data-testid="discovery-idea">{data.idea}</p>
      </div>

      {/* Conversation */}
      <div className="space-y-3" data-testid="discovery-conversation">
        {data.turns.map((t, i) => (
          <div key={i} className={t.role === "user" ? "flex justify-end" : ""}>
            {t.role === "user" ? (
              <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-[#0055FF] text-white px-4 py-2.5 text-sm" data-testid={`discovery-answer-${i}`}>{t.text}</div>
            ) : (
              <div className="max-w-[85%] rounded-2xl rounded-bl-sm border border-white/10 bg-[#0a0a0a] px-4 py-3">
                {t.text && <p className="text-slate-200 text-sm leading-relaxed">{t.text}</p>}
                {t.question && (
                  <div className="mt-2">
                    <p className="text-white font-medium">{t.question.text}</p>
                    {t.question.why && <p className="text-slate-500 text-xs mt-1">Why this matters: {t.question.why}</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Active question input */}
      {!ready && data.current_question && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid="discovery-input-panel">
          {data.current_question.suggestions?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {data.current_question.suggestions.map((s, i) => (
                <button key={i} onClick={() => setInput(s)} disabled={busy} className="text-xs text-slate-300 border border-white/10 rounded-full px-3 py-1.5 hover:border-[#0055FF] hover:text-white transition-colors duration-200 text-left" data-testid={`discovery-suggestion-${i}`}>{s}</button>
              ))}
            </div>
          )}
          <textarea
            value={input} onChange={(e) => setInput(e.target.value)} rows={3}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
            placeholder="Type your answer… (⌘/Ctrl + Enter to send)"
            className="w-full resize-none bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-[#0055FF] transition-colors duration-300"
            data-testid="discovery-answer-input"
          />
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <button onClick={send} disabled={busy || !input.trim()} className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-5 py-3 rounded-xl font-semibold disabled:opacity-40 hover:-translate-y-0.5 transition-transform duration-300" data-testid="discovery-send-btn">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send
            </button>
            <button onClick={skip} disabled={busy} className="inline-flex items-center gap-2 border border-white/15 px-4 py-3 rounded-xl text-slate-300 hover:border-white/30 transition-colors duration-300 disabled:opacity-40" data-testid="discovery-skip-btn"><SkipForward size={15} /> Skip</button>
            <button onClick={finishNow} disabled={busy} className="inline-flex items-center gap-2 border border-[#00F0FF]/30 text-[#00F0FF] px-4 py-3 rounded-xl hover:bg-[#00F0FF]/10 transition-colors duration-300 disabled:opacity-40 ml-auto" data-testid="discovery-finish-btn"><Wand2 size={15} /> Build brief now</button>
          </div>
        </div>
      )}

      {/* Product Brief */}
      {ready && <BriefCard brief={data.brief} confidence={data.confidence} generating={generating} onGenerate={generate} onSyncOS={syncOS} syncing={syncing} />}
    </Shell>
  );
}

const BackLink = () => (
  <Link to="/studio" className="font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-2" data-testid="discovery-studio-link"><ArrowLeft size={14} /> Studio</Link>
);
const Center = ({ children }) => <div className="flex items-center gap-2 text-slate-400 py-20">{children}</div>;

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-500 mb-2"><Icon size={15} className="text-[#00F0FF]" /><span className="font-mono text-[10px] uppercase tracking-widest">{title}</span></div>
    {children}
  </div>
);

function BriefCard({ brief, confidence, generating, onGenerate, onSyncOS, syncing }) {
  const tech = brief.technical_recommendation || {};
  const rev = brief.revenue_model || {};
  const musts = (brief.mvp_features || []).filter((f) => f.priority === "must");
  const laters = (brief.mvp_features || []).filter((f) => f.priority !== "must");
  return (
    <motion.div className="mt-6 space-y-4" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="discovery-brief">
      {/* Header */}
      <div className="rounded-2xl border border-[#0055FF]/30 bg-[#0055FF]/5 p-5 flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#00F0FF] mb-1">Product Brief</p>
          <h2 className="font-display font-extrabold text-2xl" data-testid="brief-app-name">{brief.app_name}</h2>
        </div>
        <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/40 px-4 py-2.5" title="Strategist confidence">
          <Gauge size={16} className="text-[#00F0FF]" />
          <span className="font-display font-bold text-xl" data-testid="brief-confidence">{brief.confidence_score ?? confidence}</span>
          <span className="text-slate-500 text-xs">/100 confidence</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Section icon={Target} title="Problem" testid="brief-problem"><p className="text-slate-300 text-sm leading-relaxed">{brief.problem_statement}</p></Section>
        <Section icon={Users} title="Target customer" testid="brief-customer"><p className="text-slate-300 text-sm leading-relaxed">{brief.target_customer}</p></Section>
      </div>

      <Section icon={Lightbulb} title="Value proposition" testid="brief-value"><p className="text-slate-300 text-sm leading-relaxed">{brief.value_proposition}</p></Section>

      <Section icon={ListChecks} title="MVP features" testid="brief-mvp">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-emerald-400 mb-2">Build first (must)</p>
            <ul className="space-y-1.5">{musts.map((f, i) => <li key={i} className="text-sm text-white flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 shrink-0" /><span>{f.name}</span></li>)}</ul>
          </div>
          {laters.length > 0 && (
            <div>
              <p className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-2">Later</p>
              <ul className="space-y-1.5">{laters.map((f, i) => <li key={i} className="text-sm text-slate-400 flex items-start gap-2"><span className="w-3.5 h-3.5 rounded-full border border-slate-600 mt-0.5 shrink-0" />{f.name}</li>)}</ul>
            </div>
          )}
        </div>
      </Section>

      <div className="grid md:grid-cols-2 gap-4">
        <Section icon={DollarSign} title="Revenue & pricing" testid="brief-revenue">
          <p className="text-white text-sm font-medium">{rev.primary}</p>
          <p className="text-slate-400 text-sm mt-1">{rev.pricing}</p>
          {rev.options?.length > 0 && <p className="text-slate-500 text-xs mt-2">Also consider: {rev.options.join(", ")}</p>}
        </Section>
        <Section icon={Cpu} title="Technical recommendation" testid="brief-tech">
          <div className="text-sm text-slate-300 space-y-1">
            {tech.frontend && <p><span className="text-slate-500">Frontend:</span> {tech.frontend}</p>}
            {tech.backend && <p><span className="text-slate-500">Backend:</span> {tech.backend}</p>}
            {tech.database && <p><span className="text-slate-500">Database:</span> {tech.database}</p>}
            {tech.integrations?.length > 0 && <p><span className="text-slate-500">Integrations:</span> {tech.integrations.join(", ")}</p>}
          </div>
        </Section>
      </div>

      <Section icon={AlertTriangle} title="Risks & assumptions" testid="brief-risks">
        <ul className="space-y-2">
          {(brief.risks_assumptions || []).map((r, i) => (
            <li key={i} className="text-sm">
              <span className={`font-mono text-[10px] uppercase tracking-widest mr-2 ${r.type === "risk" ? "text-amber-400" : "text-slate-500"}`}>{r.type}</span>
              <span className="text-slate-300">{r.text}</span>
              {r.mitigation && <span className="text-slate-500"> — {r.mitigation}</span>}
            </li>
          ))}
        </ul>
      </Section>

      <div className="grid md:grid-cols-2 gap-4">
        <Section icon={Gauge} title="Complexity & effort" testid="brief-effort">
          <p className="text-white text-sm font-medium capitalize">{brief.complexity} complexity</p>
          <p className="text-slate-400 text-sm mt-1">{brief.estimated_effort}</p>
        </Section>
        <Section icon={Rocket} title="Recommended next step" testid="brief-next">
          <p className="text-slate-300 text-sm leading-relaxed">{brief.recommended_next_step}</p>
        </Section>
      </div>

      {/* CTA */}
      <div className="pt-2 flex flex-col sm:flex-row gap-3 sm:items-center">
        <button onClick={onGenerate} disabled={generating} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white px-7 py-4 rounded-2xl font-semibold shadow-[0_0_40px_rgba(0,85,255,0.5)] disabled:opacity-40 hover:-translate-y-0.5 transition-transform duration-300" data-testid="discovery-generate-btn">
          {generating ? <Loader2 size={18} className="animate-spin" /> : <Rocket size={18} />} {generating ? "Creating project…" : "Generate project from this brief"}
        </button>
        <button onClick={onSyncOS} disabled={syncing} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-[#00F0FF]/30 text-[#00F0FF] px-6 py-4 rounded-2xl font-semibold hover:bg-[#00F0FF]/10 transition-colors duration-300 disabled:opacity-40" data-testid="discovery-sync-os-btn">
          {syncing ? <Loader2 size={18} className="animate-spin" /> : <Boxes size={18} />} {syncing ? "Syncing to ArcaOS…" : "Sync to ArcaOS"}
        </button>
      </div>
      <p className="text-slate-600 text-xs mt-2">Generate builds the app. Sync to ArcaOS creates the linked Product, Project, Bible, Business Brief, Roadmap, Milestones & Decision — no duplicates.</p>
    </motion.div>
  );
}
